import { useMutation, useQueryClient, type QueryKey } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { toast } from 'sonner';

import {
  RESEND_CONFIRM_REQUIRED_DETAIL,
  lidSendBlockedDetail,
  resendMessage,
  type Message,
} from '@/features/chat/chat.api';
import { chatKeys } from '@/features/chat/keys';
import { patchCacheWith } from '@/features/chat/useSendMessage';

/**
 * Resend a FAILED or `unknown` (in-doubt) outbound message —
 * `POST /api/v1/messages/{id}/resend`.
 *
 * This is the retry path for PERSISTED rows (a real server id), and it
 * replaces the old "re-POST text-only with file: null" retry that lost
 * media: the server rebuilds media from its content-addressed store, so a
 * failed image/document resend carries the original bytes without the
 * client re-uploading anything. Optimistic GHOST rows (id `optim-...`,
 * never persisted) keep their own retry via `useSendMessage`'s
 * `retryOfMessageId` — that path re-POSTs because there is no server row to
 * resend yet.
 *
 * `unknown` sources (2026-09 honest send status, spec 02 §4.5): the
 * customer MAY already have the message, so the hub refuses the resend
 * unless the body carries `confirm_duplicate_risk: true`. The hub checks
 * that confirm-required 409 LAST — after the LID guard and the
 * not-connected guard — so callers (MessageBubble's `ResendActions`, FX2-F)
 * resend an `unknown` source WITHOUT the confirmation on the FIRST attempt
 * and let the hub's response drive the ask, rather than pre-empting it
 * client-side (asking up front could make the operator confirm a duplicate
 * risk for a resend that was actually going to be blocked for an unrelated
 * reason). Whenever the hub DOES answer the confirm-required 409 — a source
 * already cached as `unknown`, or one our cache still called `failed` while
 * the server's `unknown` wins (e.g. a historical in-doubt row migration
 * 0015 reclassified while this console stayed open) — `onConfirmRequired`
 * hands the refused variables back so the caller can ask through
 * `ResendConfirmDialog` and retry the SAME target with
 * `confirmDuplicateRisk: true`.
 *
 * No optimistic write: the source bubble already shows its status, and the
 * server mints a FRESH row (new id, `resend_of_message_id` = source id)
 * whose target conversation may differ from the source's (cross-account
 * resend). The fresh row lands in the cache on the 201.
 *
 * Cache strategy — established from `projection.ts`'s `message.new`
 * handling: the WS projector inserts through `patchCacheWith` →
 * `mergeServerMessage`, which patches IN PLACE any row matching by
 * `client_ref` OR `id` and only otherwise prepends. Inserting the 201 body
 * through the SAME exported helper therefore commutes with the WS echo in
 * either order: whichever lands second finds the row (by id at minimum) and
 * patches in place — a duplicate bubble is impossible by construction. The
 * thread slot is patched too when the resend originated from an open merged
 * thread (`threadKey`), mirroring both `useSendMessage.onSuccess` and the
 * projector's member-conversation mirror.
 *
 * Consistent with `useSendMessage`: NO `invalidateQueries`, ever — the
 * conversation list re-orders via the WS `conversation.updated` event, and
 * an invalidate would refetch-flicker the messages window.
 */

export interface ResendMessageVariables {
  /** The FAILED / `unknown` source message's server id. */
  messageId: string;
  /** Another member conversation of the SAME customer to resend through
   *  (cross-account). Omit/null for a same-conversation resend. */
  targetConversationId?: string | null;
  /** Set when the resend originates from an open merged phone-thread — the
   *  fresh row is mirrored into `chatKeys.threadMessages(threadKey)` in
   *  addition to the target conversation's slot. NOT sent to the server. */
  threadKey?: string | null;
  /** Display label of the TARGET account (cross-account resends only) —
   *  used for the success toast「已通过 {label} 重发」. NOT sent to the
   *  server. */
  accountLabel?: string | null;
  /** The operator confirmed the duplicate risk of resending an `unknown`
   *  source (`ResendConfirmDialog`) — sent as `confirm_duplicate_risk: true`.
   *  Irrelevant for a `failed` source; omitted from the body unless true. */
  confirmDuplicateRisk?: boolean;
}

export interface UseResendMessageOptions {
  /** The hub refused the resend with the confirm-required 409 (see
   *  {@link isResendConfirmRequired}): the source is `unknown`. Receives
   *  the refused call's variables, so the caller can ask the operator and
   *  resend the same target with `confirmDuplicateRisk: true`. Without it
   *  the hook falls back to a toast. */
  onConfirmRequired?: (variables: ResendMessageVariables) => void;
}

/**
 * Is `error` the hub's confirm-required refusal — `409` with EXACTLY
 * {@link RESEND_CONFIRM_REQUIRED_DETAIL} (the source is `unknown` and the
 * body lacked `confirm_duplicate_risk: true`)? Matched on the exported
 * detail, never a re-typed copy of it.
 */
export function isResendConfirmRequired(error: unknown): boolean {
  return (
    isAxiosError<{ detail?: unknown }>(error) &&
    error.response?.status === 409 &&
    error.response.data?.detail === RESEND_CONFIRM_REQUIRED_DETAIL
  );
}

export function useResendMessage(options: UseResendMessageOptions = {}) {
  const queryClient = useQueryClient();
  const { onConfirmRequired } = options;

  return useMutation<Message, unknown, ResendMessageVariables>({
    mutationFn: ({ messageId, targetConversationId, confirmDuplicateRisk }) =>
      resendMessage(messageId, targetConversationId, { confirmDuplicateRisk }),

    onSuccess: async (serverMessage, variables) => {
      // Stomp protection — mirror useSendMessage.onMutate's discipline: a
      // messages/thread refetch already on the wire when the 201 lands
      // would resolve AFTER the patches below and overwrite them with a
      // pre-resend snapshot. Cancel in-flight fetches for every slot this
      // patch touches, BEFORE writing.
      const keys: QueryKey[] = [chatKeys.messages(serverMessage.conversation_id)];
      if (variables.threadKey) {
        keys.push(chatKeys.threadMessages(variables.threadKey));
      }
      for (const k of keys) {
        await queryClient.cancelQueries({ queryKey: k });
      }

      // Insert keyed by the SERVER's conversation_id, not the variables' —
      // a same-conversation resend omits targetConversationId entirely, and
      // the 201 body is authoritative about where the fresh row lives.
      patchCacheWith(
        queryClient,
        chatKeys.messages(serverMessage.conversation_id),
        serverMessage,
      );
      if (variables.threadKey) {
        patchCacheWith(
          queryClient,
          chatKeys.threadMessages(variables.threadKey),
          serverMessage,
        );
      }

      // The hub AWAITS the adapter, so a synchronous gateway failure still
      // answers 201 — with a `failed` fresh row. That is not a success to
      // the operator: surface the failure copy instead (the row above still
      // lands in the cache, so a failed bubble with its own Retry renders).
      // Stable id: a rapid retry burst replaces the one toast.
      if (serverMessage.delivery_status === 'failed') {
        toast('重发未送达', { id: 'resend-not-delivered' });
        return;
      }
      // `unknown`: the fresh row's frame went out but no ack came back — the
      // customer MAY have it. Not a success either (spec 02 §4.6); the fresh
      // bubble carries the in-doubt state and its own confirm-to-resend path.
      if (serverMessage.delivery_status === 'unknown') {
        toast('重发结果未知', { id: 'resend-result-unknown' });
        return;
      }
      toast(
        variables.accountLabel
          ? `已通过 ${variables.accountLabel} 重发`
          : '已重发',
      );
    },

    onError: (error, variables) => {
      // The confirm-required 409: the source is `unknown`, and this call did
      // not carry the duplicate-risk confirmation (our cache still said
      // `failed`). Not an error to report — a question to ask: hand the
      // variables back so the caller opens ResendConfirmDialog for the same
      // target. Toast only when nobody is listening.
      if (isResendConfirmRequired(error)) {
        if (onConfirmRequired) {
          onConfirmRequired(variables);
          return;
        }
        toast('发送结果未知，请确认后再重发', { id: 'resend-confirm-required' });
        return;
      }
      // 409 = a STATE conflict; the FastAPI `{"detail": ...}` body is the
      // discriminator (same idiom as useReactToMessage's onError):
      //   - "message has already been resent" — the one-resend-per-source
      //     guard; a double-click or a second agent beat us to it.
      //   - LID send guard — the customer is known only by WhatsApp LID and
      //     the resend would be misrouted; the detail is a ready-made Chinese
      //     instruction (reply from the phone), toasted verbatim with
      //     useSendMessage's id and duration.
      //   - "... is not connected (status: ...)" — the target account can't
      //     send right now; reuse useSendMessage's exact copy AND toast id,
      //     so a send-then-resend burst against a dead line replaces the
      //     one toast instead of stacking two wordings of the same fact.
      //   - source-not-failed / media-no-longer-available fall through to
      //     the generic toast below.
      if (isAxiosError<{ detail?: unknown }>(error) && error.response?.status === 409) {
        const detail = error.response.data?.detail;
        const lidDetail = lidSendBlockedDetail(error);
        if (lidDetail) {
          toast(lidDetail, { id: 'lid-send-blocked', duration: 10_000 });
          return;
        }
        if (typeof detail === 'string' && detail.includes('already been resent')) {
          toast('该消息已重发过', { id: 'resend-already-resent' });
          return;
        }
        if (typeof detail === 'string' && detail.includes('not connected')) {
          toast('该账号当前未连接，无法发送', { id: 'send-account-not-connected' });
          return;
        }
      }
      // Everything else (404 / 400 structural / other 409 causes / network):
      // unlike a fresh send there is NO failed bubble to carry the news — a
      // silent miss would read as "the click did nothing" — so a quiet
      // generic toast is the surface. Stable id: rapid retries replace it.
      toast('重发失败', { id: 'resend-failed' });
    },
  });
}
